/** Shared client types for the GBP Agent surfaces (feed, cards, config). */

import type { GoogleTextField } from '@/config/googleTextLimits';
import { clockOf } from '@/components/agent-ui/timeFormat';
// The worker's words for where a change stands, so this card, the Activity
// line and the chips say one thing (Anand, 25 Sep 26, Zerebral IT Solutions).
import { changeDoneLine, isTrackedChange, siteDoneLine } from '../../../scripts/gbp-agent/change-wording';
import { keywordsTrackLines } from '../../../scripts/gbp-agent/keyword-track-words';

/**
 * Input field a card can render inline so the owner never has to hunt for
 * where to act (action_data.inputs). Submitted values land in
 * action_data.user_input and, when the card carries an execution payload,
 * the agent applies them to the real profile.
 */
export interface ActionInputSpec {
  key: string;
  label: string;
  /**
   * 'list': one item per row with a plus to add the next (Anand, 21 Sep 26:
   * not a comma separated box). The answer travels as one line per item.
   */
  type?: 'text' | 'tel' | 'url' | 'textarea' | 'checkbox_group' | 'radio_group' | 'list';
  placeholder?: string;
  required?: boolean;
  /** Prefill (e.g. the agent's suggested short name). */
  value?: string;
  /**
   * Options for a checkbox_group. Used where only the owner can say what is
   * true, such as the Google profile attributes (wheelchair access, parking,
   * online appointments). Selected values submit as a comma separated string.
   */
  options?: Array<{ value: string; label: string; hint?: string }>;
  /**
   * The most boxes a checkbox_group takes; the rest wait until one is
   * unticked (the searches card: the keywords the agent has room to read).
   */
  max_selected?: number;
  /**
   * Which Google cap the answer is measured against (the description
   * approval card sets 'description'). The card shows a counter and the
   * input stops at the cap (Shardul, 18 Sep 26).
   */
  limit_field?: GoogleTextField;
  /**
   * Letters that fit best: past this the card warns but lets the owner go on
   * typing, up to max_length (the short name: 18, then 48; Anand, 21 Sep 26).
   */
  soft_limit?: number;
  /** A hard stop on how many letters the box takes. */
  max_length?: number;
  /**
   * Shown (and required) only while another input holds this value: the
   * domain box on the website card appears when the owner picks their own
   * domain (Anand, 26 Sep 26).
   */
  show_when?: { key: string; value: string };
}

export interface AgentAction {
  id: string;
  workspace_id?: string;
  action_type: string;
  category: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string | null;
  why_it_matters: string | null;
  expected_impact: string | null;
  status: string;
  action_data: Record<string, unknown> | null;
  execution_payload?: Record<string, unknown> | null;
  execution_result?: Record<string, unknown> | null;
  error_details?: string | null;
  // 'superseded': a newer card took its place (open-cards.ts, migration 20260929000000).
  completion_source?: 'user_marked' | 'agent_verified' | 'superseded' | null;
  /**
   * How this card ends, when the row carries its own answer
   * (scripts/gbp-agent/verify-cards.ts). The page reads only the name, to
   * decide whether Check again has anything to ask (cardRecheck.ts).
   */
  verification_check?: { type?: string } | null;
  executed_at?: string | null;
  execution_mode: 'agent' | 'user';
  assigned_to: 'owner' | 'ops';
  due_at: string | null;
  created_at: string;
  completed_at: string | null;
}

/**
 * The line a card taken while the agent is paused says ("Goes out on a paid
 * plan", "Goes out when your plan is active"): it is approved, waits, and goes
 * out once the agent runs again (supabase/functions/_shared/paused-hold.ts,
 * WO-71). Null on every other card.
 */
export function heldLineOf(action: Pick<AgentAction, 'status' | 'execution_payload'>): string | null {
  if (action.status !== 'approved') return null;
  const hold = (action.execution_payload as { paused_hold?: { line?: unknown } } | null | undefined)?.paused_hold;
  return hold && typeof hold.line === 'string' && hold.line ? hold.line : null;
}

/**
 * Does this card say the agent has lost Google for the location (signed out,
 * or the account no longer lists the profile)? Every reconnect_google card
 * does but one: the day 1 check's "Google is not sending your views and
 * calls" (scripts/gbp-agent/facts/day-one.ts) is raised as a reconnect card
 * while the sign-in works, and closes once performance data arrives
 * (performance_flowing). Read as a sign-out, it put "Google connection ·
 * Signed out" over Shree Car Sales minutes after the agent had read its
 * profile and reviews (QA 9e8c3cd4, Manu, 30 Sep 26).
 */
export function isLostConnectionCard(action: Pick<AgentAction, 'action_type' | 'verification_check'>): boolean {
  return action.action_type === 'reconnect_google' && action.verification_check?.type !== 'performance_flowing';
}

/**
 * Plain-language summary of what an executed card actually did on the
 * profile. Never raw data; unknown result shapes fall back to a generic
 * confirmation line.
 */
export function humanizeActionOutcome(action: AgentAction): string[] {
  const lines: string[] = [];
  const result = (action.execution_result ?? {}) as Record<string, any>;
  const userInput = ((action.action_data ?? {}) as Record<string, any>).user_input as
    | Record<string, string>
    | undefined;

  if (action.status === 'failed') {
    lines.push(action.error_details ? `It failed: ${action.error_details}` : 'It failed. The agent will surface it again.');
    return lines;
  }

  if (action.status === 'executed') {
    const bridge = String(((action.execution_payload ?? {}) as Record<string, any>).bridge ?? result.bridge ?? '');
    // A change on Google says where it stands, read off the verification
    // record the verify pass keeps (never the write's own echo): "Sent to
    // Google, not showing on your profile yet" until the pass sees it, then
    // where the owner can see it. "Applied to your Google profile" was said
    // of services the owner could not find (Anand, 25 Sep 26, Zerebral).
    if (isTrackedChange(bridge)) {
      const line = changeDoneLine(bridge, result);
      if (line) lines.push(line);
      if (bridge === 'description' && typeof result.applied_description === 'string') {
        lines.push(`New description: "${String(result.applied_description).slice(0, 240)}${String(result.applied_description).length > 240 ? '...' : ''}"`);
      }
      return lines;
    }
    switch (bridge) {
      case 'review_reply':
        if (result.skipped === 'review_not_found_on_google') {
          lines.push('This review is no longer visible on Google, so no reply was needed. The card was closed.');
        } else if (result.skipped === 'already_replied') {
          lines.push('This review already had a reply on Google, nothing more was needed.');
        } else {
          lines.push('The reply was posted on Google.');
          if (typeof result.result?.reply === 'string') lines.push(`Reply: "${result.result.reply}"`);
        }
        break;
      case 'short_name':
        lines.push(`Short name "${result.short_name ?? ''}" saved; your generated content uses it from now on.`);
        break;
      // Track these on the searches card: what is tracked now, and in whose place.
      case 'keywords_track':
        lines.push(...keywordsTrackLines(result));
        break;
      // The domain attach ends with two lines the owner has to type at their
      // registrar, and the Done card used to say only that it was applied
      // (Shardul, 22 Sep 26: "i didnt got any dns records").
      case 'website_domain_attach': {
        const domain = typeof result.domain === 'string' ? result.domain : 'your domain';
        const records = Array.isArray(result.records)
          ? (result.records as Array<{ type?: string; name?: string; value?: string }>)
          : [];
        lines.push(`${domain} is set up on your Fly website. Add this record where the domain was bought:`);
        for (const r of records) {
          if (!r?.type || !r?.value) continue;
          lines.push(`Type: ${r.type}, Host or Name: ${r.name ?? '@'}, Value: ${r.value}`);
        }
        lines.push('The agent checks every pass and confirms when it is live. It can take a few hours to spread.');
        break;
      }
      case 'website_feedback':
        // A request for pictures is held until the owner sends them, so this
        // card is not a change that was made (Manu, QA, 22 Sep 26: "Agent did
        // not ask me to upload images and updated the status as done").
        if (result.awaiting_photos === true) {
          lines.push('Waiting for your photos. Nothing has changed on the website yet: the agent has no pictures of your place and cannot take any.');
          lines.push('The card "Add the photos for your website" on your home page is where to send them.');
        } else {
          // Live on the website only once the published page showed it
          // (site-live.ts). A card from before the page check keeps its words.
          lines.push(siteDoneLine(result) ?? 'Your website was updated with what you asked for.');
        }
        break;
      // The older reviews clean-up started or stopped: the worker's own line, how far it got (bridges/review-backlog.ts).
      case 'review_backlog_start':
      case 'review_backlog_stop':
        lines.push(typeof result.line === 'string' && result.line ? result.line : 'Done.');
        break;
      default:
        // Not a change on Google: nothing here may say it was applied there.
        lines.push('Done.');
    }
    return lines;
  }

  // Completed (user task or acknowledged card).
  // A card another took over says why, not "You marked this done": the AI
  // assistant card's checks moved into the website check card (25 Sep 26).
  const closedReason = ((action.action_data ?? {}) as Record<string, unknown>).closed_reason;
  if (action.completion_source === 'superseded') {
    lines.push(typeof closedReason === 'string' && closedReason.trim() ? closedReason.trim() : 'A newer card took its place.');
  } else if (action.completion_source === 'agent_verified') {
    // A card that closed itself says why when it wrote down why (the searches
    // card with nothing left to propose, 1a0e77b9, 25 Sep 26).
    lines.push(typeof closedReason === 'string' && closedReason.trim() ? closedReason.trim() : 'The agent verified this is done.');
  } else if (userInput && Object.keys(userInput).length > 0) {
    lines.push('You provided the details and the task was closed.');
  } else if (action.execution_mode === 'agent') {
    lines.push('You acknowledged this recommendation. The agent will automate it in a later phase; until then it can be done from the profile editor.');
  } else {
    lines.push('You marked this done.');
  }
  return lines;
}

export interface AgentEvent {
  id: string;
  event_type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  action_id?: string | null;
  created_at: string;
  visibility?: 'all' | 'ops';
}

export interface RankKeywordSummary {
  keyword_id: string;
  keyword: string;
  avg: number | null;
  previous: number | null;
  trend: 'improving' | 'declining' | 'stable' | null;
  gap_to_top3: number | null;
  closest_ahead: { name: string; gap: number } | null;
  visibility: number | null;
  /** Grid coverage of the latest reading, when the grid was stored. */
  pack_points?: number | null;
  in_pack_at?: number | null;
  found_at?: number | null;
  /** When the latest reading was taken. */
  checked_at?: string | null;
}

export interface RankSummary {
  per_keyword?: RankKeywordSummary[];
  below_target?: RankKeywordSummary[];
  last_aggregated_at?: string;
}

/** Plain-language status labels; raw status slugs never face the owner. */
export function statusLabel(status: string): string {
  const map: Record<string, string> = {
    suggested: 'Suggested',
    pending_approval: 'Waiting for approval',
    pending_user: 'Waiting for you',
    approved: 'Queued to apply',
    executing: 'Applying',
    executed: 'Done',
    completed: 'Done',
    failed: 'Could not apply',
    rejected: 'Declined',
    skipped: 'Dismissed',
    expired: 'Expired',
    backlog: 'Up next',
  };
  return map[status] ?? status;
}

/** 'change': a finished card's text applied again with new words (Done for you, done-card-change.ts). */
export type RespondDecision = 'approve' | 'reject' | 'done' | 'dismiss' | 'submit' | 'change';

/** Values collected from a card's inline inputs, passed with 'submit'. */
export type RespondInputs = Record<string, string>;

/** A time zone the browser knows, or undefined (then the browser's own is used). */
function knownZone(timeZone: string | null | undefined): string | undefined {
  const tz = String(timeZone ?? '').trim();
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
}

/**
 * dd Mon yy, the required display format. The locale writes "Sept"; the rule
 * is three letters. With a time zone (the location's own, WO-65), the day in
 * that zone; without one, or with one the browser does not know, the
 * browser's.
 */
export function formatShortDate(value: string | Date, timeZone?: string | null): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const zone = knownZone(timeZone);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', ...(zone ? { timeZone: zone } : {}) }).replace('Sept', 'Sep');
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * An ISO week key ("2026-W41", as the weekly checks write data.week) as the
 * owner reads it, in dd Mon yy parts: "05 to 11 Oct 26", "28 Sep to 04 Oct 26",
 * "29 Dec 25 to 04 Jan 26" (walk 05 Oct 26, item 37: Activity printed
 * "Week: 2026-W41"). Monday of week 1 is the Monday on or before 4 Jan. The
 * days are worked out at UTC noon and read with UTC getters, so no local zone
 * can move a day. Null when the key does not parse.
 */
export function isoWeekRange(key: string): string | null {
  const m = /^(\d{4})-W(\d{2})$/.exec(key);
  if (!m) return null;
  const year = Number(m[1]);
  const week = Number(m[2]);
  if (week < 1 || week > 53) return null;
  const DAY = 86_400_000;
  const jan4 = Date.UTC(year, 0, 4, 12);
  const isoDay = new Date(jan4).getUTCDay() || 7;
  const monday = new Date(jan4 - (isoDay - 1) * DAY + (week - 1) * 7 * DAY);
  const sunday = new Date(monday.getTime() + 6 * DAY);
  const parts = (d: Date) => ({
    dd: String(d.getUTCDate()).padStart(2, '0'),
    mon: SHORT_MONTHS[d.getUTCMonth()],
    yy: String(d.getUTCFullYear() % 100).padStart(2, '0'),
  });
  const a = parts(monday);
  const b = parts(sunday);
  if (a.yy !== b.yy) return `${a.dd} ${a.mon} ${a.yy} to ${b.dd} ${b.mon} ${b.yy}`;
  if (a.mon !== b.mon) return `${a.dd} ${a.mon} to ${b.dd} ${b.mon} ${b.yy}`;
  return `${a.dd} to ${b.dd} ${b.mon} ${b.yy}`;
}

/**
 * dd Mon yy, hh:mm - for activity timestamps, and the compact stamp on every
 * frame (Anand, 13 Oct 26: "whenever we show Frames please show a compact
 * date / time when it was created"), in the location's time zone when the
 * page knows it, else the browser's.
 */
export function formatShortDateTime(value: string | Date, timeZone?: string | null): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const zone = knownZone(timeZone);
  // The clock follows Settings, Appearance, Time (12 or 24 hour).
  return `${formatShortDate(date, zone)}, ${clockOf(date, zone)}`;
}

/**
 * The compact stamp on every frame, wherever a frame is shown (WO-65): when
 * it was made, "Designed 13 Oct 26, 14:05", or "Refined ..." for a frame
 * refined from another one. The owner's pages pass the location's time zone
 * when the read names it; the ops console the browser's.
 */
export function frameCreatedStamp(frame: { created_at: string; refined_from?: string | null }, timeZone?: string | null): string {
  return `${frame.refined_from ? 'Refined' : 'Designed'} ${formatShortDateTime(frame.created_at, timeZone)}`;
}

/**
 * Turns an event's data blob into plain-language lines an owner can read.
 * Unknown keys are OMITTED, never dumped as raw JSON (user rule, 7 Aug 26:
 * end users cannot act on raw data).
 */
export function humanizeEventData(data: Record<string, unknown> | null | undefined): string[] {
  if (!data) return [];
  const lines: string[] = [];

  // Only the profile audit event writes data.score (scripts/gbp-agent/checks/seo.ts, the
  // audit_completed event; checked 05 Oct 26), so it reads as the audit, never a "score" (T19).
  if (typeof data.score === 'number') lines.push(`Profile audit: ${data.score} of 100`);
  if (typeof data.avg_rank === 'number') lines.push(`Average rank across money keywords: ${data.avg_rank}`);
  if (typeof data.top3_count === 'number') lines.push(`Keywords in the top 3: ${data.top3_count}`);
  if (Array.isArray(data.per_keyword)) {
    for (const k of data.per_keyword as any[]) {
      if (k?.keyword) {
        const move =
          k.previous != null && k.avg != null
            ? k.avg < k.previous
              ? `, up ${(k.previous - k.avg).toFixed(1)}`
              : k.avg > k.previous
                ? `, down ${(k.avg - k.previous).toFixed(1)}`
                : ', steady'
            : '';
        lines.push(`"${k.keyword}": rank ${k.avg ?? 'not found yet'}${move}`);
      }
    }
  }
  if (data.this_week && typeof data.this_week === 'object') {
    const w = data.this_week as any;
    const views = (w.search_impressions ?? 0) + (w.maps_impressions ?? 0);
    lines.push(`This week: ${views} views, ${w.total_actions ?? 0} customer actions, ${w.call_clicks ?? 0} calls`);
  }
  if (Array.isArray(data.improvement_areas) && data.improvement_areas.length) {
    const friendly: Record<string, string> = {
      visibility: 'getting seen more',
      conversion: 'turning views into actions',
      local_intent: 'attracting nearby customers',
      keep_momentum: 'keeping the momentum',
    };
    lines.push(
      `Focus areas: ${(data.improvement_areas as string[]).map((a) => friendly[a] ?? a).join(', ')}`,
    );
  }
  if (data.search_focus && typeof data.search_focus === 'object') {
    const sf = data.search_focus as any;
    if (sf.movers?.length) lines.push(`Searches gaining: ${sf.movers.map((m: any) => `"${m.keyword}"`).join(', ')}`);
    if (sf.stalled?.length) lines.push(`Searches slipping: ${sf.stalled.map((m: any) => `"${m.keyword}"`).join(', ')}`);
  }
  if (typeof data.week === 'string') {
    // The week as dates; a key that does not parse is left out, never shown raw.
    const range = isoWeekRange(data.week);
    if (range) lines.push(`Week: ${range}`);
  }
  if (typeof data.nudge_count === 'number') lines.push(`Reminder number ${data.nudge_count}`);
  if (data.email_sent === true) lines.push('Email sent');

  return lines;
}

/** One profile edit the agent sent to Google, as the home's chips show it. */
export interface ProfileUpdateItem {
  action_id: string;
  title: string;
  field: string;
  value: string;
  sent_at: string;
  live_at: string | null;
  checked_at: string | null;
  status: 'live' | 'pending' | 'stale';
  pending_mask: string | null;
  checks: number;
  note: string | null;
}

/** The three lists behind the Updated / Pending at Google / Not live chips. */
export interface ProfileUpdates {
  recent: ProfileUpdateItem[];
  pending: ProfileUpdateItem[];
  stale: ProfileUpdateItem[];
  as_of: string;
}
